/**
 * Circle - Full circle curve
 *
 * Rhino-style: Circles can reference Point objects via pointIds.
 * - centerPointId: The center point
 * - radiusPointId: Optional point on the circle that defines the radius
 * When the referenced points move, the circle updates automatically.
 */

import { GeometryType } from '../base/GeometryBase';
import { Vector } from '../base/Vector';
import { CurveBase } from './CurveBase';
import { Arc } from './Arc';

export class Circle extends CurveBase {
  /**
   * Create a circle
   * @param {Vector|{x,y,z}} center - Center point (or null if using pointIds)
   * @param {number} radius - Radius in mm
   * @param {Object} attributes - Including optional centerPointId and radiusPointId
   */
  constructor(center, radius, attributes = {}) {
    super(GeometryType.CIRCLE, 'cir', attributes);

    // Extract point IDs from attributes
    this._centerPointId = attributes.centerPointId || null;
    this._radiusPointId = attributes.radiusPointId || null;

    if (center) {
      this.center = center instanceof Vector ? center : Vector.fromObject(center);
    } else {
      this.center = new Vector(0, 0, 0);
    }
    this.radius = radius || 0;
    this.plane = 'XY';
  }

  /**
   * Get center point ID
   */
  get centerPointId() {
    return this._centerPointId;
  }

  /**
   * Set center point ID
   */
  set centerPointId(id) {
    this._centerPointId = id;
    this.touch();
  }

  /**
   * Get radius point ID (point on circle)
   */
  get radiusPointId() {
    return this._radiusPointId;
  }

  /**
   * Set radius point ID
   */
  set radiusPointId(id) {
    this._radiusPointId = id;
    this.touch();
  }

  /**
   * Get point IDs array [centerPointId, radiusPointId?]
   */
  get pointIds() {
    const ids = [];
    if (this._centerPointId) ids.push(this._centerPointId);
    if (this._radiusPointId) ids.push(this._radiusPointId);
    return ids.length > 0 ? ids : null;
  }

  /**
   * Check if this circle uses point references
   */
  get usesPointReferences() {
    return !!this._centerPointId;
  }

  /**
   * Update internal values from external point state
   * @param {Function} getPoint - Function to get point by ID (returns {x, y, z})
   */
  updateFromPoints(getPoint) {
    if (!this.usesPointReferences) return;

    const centerPt = getPoint(this._centerPointId);
    if (centerPt) {
      this.center = new Vector(centerPt.x, centerPt.y, centerPt.z || 0);
    }

    // If we have a radius point, calculate radius from it
    if (this._radiusPointId) {
      const radiusPt = getPoint(this._radiusPointId);
      if (radiusPt && centerPt) {
        const rp = new Vector(radiusPt.x, radiusPt.y, radiusPt.z || 0);
        this.radius = this.center.distanceTo(rp);
      }
    }
  }

  /**
   * Get center point (same as center for Circle, for consistent API)
   */
  get centerPoint() {
    return this.center;
  }

  /**
   * Create circle from 3 points
   * @param {Vector} p1
   * @param {Vector} p2
   * @param {Vector} p3
   * @param {Object} attributes
   * @returns {Circle|null}
   */
  static from3Points(p1, p2, p3, attributes = {}) {
    const start = p1 instanceof Vector ? p1 : Vector.fromObject(p1);
    const through = p2 instanceof Vector ? p2 : Vector.fromObject(p2);
    const end = p3 instanceof Vector ? p3 : Vector.fromObject(p3);

    const ax = start.x, ay = start.y;
    const bx = through.x, by = through.y;
    const cx = end.x, cy = end.y;

    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(d) < 1e-10) {
      return null;
    }

    const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
    const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;

    const center = new Vector(ux, uy, start.z);
    const radius = center.distanceTo(start);

    return new Circle(center, radius, attributes);
  }

  /**
   * Create circle from center and point on circle
   * @param {Vector} center
   * @param {Vector} pointOnCircle
   * @param {Object} attributes
   * @returns {Circle}
   */
  static fromCenterAndPoint(center, pointOnCircle, attributes = {}) {
    const c = center instanceof Vector ? center : Vector.fromObject(center);
    const p = pointOnCircle instanceof Vector ? pointOnCircle : Vector.fromObject(pointOnCircle);
    const radius = c.distanceTo(p);
    return new Circle(c, radius, attributes);
  }

  get isClosed() {
    return true;
  }

  get length() {
    return 2 * Math.PI * this.radius;
  }

  get circumference() {
    return this.length;
  }

  get area() {
    return Math.PI * this.radius * this.radius;
  }

  get diameter() {
    return this.radius * 2;
  }

  pointAt(t) {
    const angle = t * 2 * Math.PI;
    return new Vector(
      this.center.x + this.radius * Math.cos(angle),
      this.center.y + this.radius * Math.sin(angle),
      this.center.z
    );
  }

  tangentAt(t) {
    const angle = t * 2 * Math.PI;
    return new Vector(
      -Math.sin(angle),
      Math.cos(angle),
      0
    );
  }

  /**
   * Check if point is inside circle
   * @param {Vector} point
   * @returns {boolean}
   */
  containsPoint(point) {
    const p = point instanceof Vector ? point : Vector.fromObject(point);
    return this.center.distanceTo(p) <= this.radius;
  }

  /**
   * Get point on circle at angle
   * @param {number} angle - Angle in radians
   * @returns {Vector}
   */
  pointAtAngle(angle) {
    return new Vector(
      this.center.x + this.radius * Math.cos(angle),
      this.center.y + this.radius * Math.sin(angle),
      this.center.z
    );
  }

  /**
   * Get tangent line at angle
   * @param {number} angle - Angle in radians
   * @returns {{point: Vector, direction: Vector}}
   */
  tangentAtAngle(angle) {
    return {
      point: this.pointAtAngle(angle),
      direction: new Vector(-Math.sin(angle), Math.cos(angle), 0),
    };
  }

  /**
   * Offset circle (concentric)
   * @param {number} distance - Positive = outward
   * @returns {Circle|null}
   */
  offset(distance) {
    const newRadius = this.radius + distance;
    if (newRadius <= 0) return null;
    return new Circle(this.center.clone(), newRadius, { ...this.attributes });
  }

  /**
   * Convert to arc (full circle)
   * @returns {Arc}
   */
  toArc() {
    return new Arc(this.center.clone(), this.radius, 0, 2 * Math.PI, { ...this.attributes });
  }

  /**
   * Split into arcs
   * @param {number} count - Number of arcs
   * @returns {Arc[]}
   */
  splitIntoArcs(count) {
    const arcs = [];
    const angleStep = (2 * Math.PI) / count;
    for (let i = 0; i < count; i++) {
      arcs.push(new Arc(
        this.center.clone(),
        this.radius,
        i * angleStep,
        (i + 1) * angleStep,
        { ...this.attributes }
      ));
    }
    return arcs;
  }

  clone() {
    const circle = new Circle(this.center.clone(), this.radius, { ...this.attributes });
    circle._centerPointId = this._centerPointId;
    circle._radiusPointId = this._radiusPointId;
    return circle;
  }

  transform({ translate, rotate, scale, mirror }) {
    let center = this.center.clone();
    let radius = this.radius;

    if (scale) {
      const s = typeof scale === 'number' ? scale : 1;
      center = center.multiply(s);
      radius *= s;
    }

    if (rotate) {
      center = center.rotate2D(rotate);
    }

    if (translate) {
      center = center.add(Vector.fromObject(translate));
    }

    if (mirror) {
      center = center.mirror(Vector.fromObject(mirror));
    }

    return new Circle(center, radius, { ...this.attributes });
  }

  getBoundingBox() {
    return {
      min: {
        x: this.center.x - this.radius,
        y: this.center.y - this.radius,
        z: this.center.z,
      },
      max: {
        x: this.center.x + this.radius,
        y: this.center.y + this.radius,
        z: this.center.z,
      },
    };
  }

  toJSON() {
    return {
      ...super.toJSON(),
      center: this.center.toObject(),
      radius: this.radius,
      plane: this.plane,
      centerPointId: this._centerPointId || null,
      radiusPointId: this._radiusPointId || null,
    };
  }

  static fromJSON(json) {
    const circle = new Circle(
      Vector.fromObject(json.center),
      json.radius,
      {
        ...json.attributes,
        centerPointId: json.centerPointId || null,
        radiusPointId: json.radiusPointId || null,
      }
    );
    circle.id = json.id;
    circle.plane = json.plane;
    circle.createdAt = json.createdAt;
    circle.modifiedAt = json.modifiedAt;
    return circle;
  }
}

export default Circle;
